import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { MessageRef } from './MessageRef';
import { Messages, newMessage } from "../../../model/Message";
import messagesQuery from "../../../firestore/Messages.query";
import { alertOpen, AlertValue } from "../../../features/alert.slice";
import { auth } from "../../../firebase";
import { TopicsMap } from "../../../model/TopicsMap";
import companiesQuery from "../../../firestore/Companies.query";
import { ReplyButton } from "../../../model/ReplyButton";
import { OnlineUrls } from "../../../model/OnlineUrls";
import { Spiner } from "../../../components/spinner/Spiner";
import { Company } from "../../../model/Companies";
import { MessageEditModal } from "./MessageEditModal";
import Button from "../../../components/button/Button";
import { createEvenReplyButtons } from "../../../utility/CreateEventReplyButtonUtility";
import { MessagePreviewModal } from "./MessagePreviewModal";
import { MessagingCommonFunctions } from "../../../controller/MessagingCommonFunctionsController";
import { ConstMessage } from "../../../config/const";

export const MessageInfo = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;
  const messageCommonFunctions = new MessagingCommonFunctions();
  // URLパラメータからmessageId取得
  const { messageId } = useParams<{ messageId: string }>();
  // URLパラメータからtenantId取得
  const { tenantId } = useParams<{ tenantId: string }>();

  const isDisabled = useRef(false);

  const [message, setMessage]  = useState<Messages>(newMessage);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [isOpenPreview, setIsOpenPreview] = useState<boolean>(false);
  const [topicsMap, setTopicsMap] = useState<TopicsMap[]>([]);
  const [company, setCompany] = useState<Company>();
  const [replyButtonSelected, setReplyButtonSelected] = useState<ReplyButton | undefined>();
  const [companyReplyButtonList, setCompanyReplyButtonList] = useState<ReplyButton[]>([]);
  const [defaultReplyButtonList, setDefaultReplyButtonList] = useState<ReplyButton[]>([]);
  const [eventReplyButtonList, setEventReplyButtonList] = useState<ReplyButton[]>([]);
  const [onlineUrlList, setOnlineUrlList] = useState<OnlineUrls[]>([]);
  const [onlineUrlSelected, setOnlineUrlSelected] = useState<string>();
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [base64Image, setBase64Image] = useState<string>('');
  const [previewData, setPreviewData] = useState<Messages>();

  useEffect(() => {
    if(messageId){
      const f = async () => {
        // 団体を作成
        const company = await companiesQuery.getCompany(tenantId!);
        setCompany(company);
        // グループ一覧を作成
        const groupList = await companiesQuery.getTenantGroups(tenantId!);

        const topicsMap: TopicsMap[] = [{
          id: company.displayId,
          name: company.name,
          nameKana: company.nameKana,
          userCount: company.usersCount,
          deleteFlg: company.deleteFlg
        }];
        groupList.forEach((group) => {
          topicsMap.push({
            id: group.id,
            name: group.name,
            nameKana: group.nameKana,
            userCount: group.userCount,
            deleteFlg: group.deleteFlg
          })
        })
        setTopicsMap(topicsMap);

        // お知らせ取得
        const message = await messagesQuery.getMessage(messageId);
        const topic = await messagesQuery.getTopic(messageId);
        message.topic = topic.map(t => t.id);

        // お知らせに付与された返信ボタン取得
        const replyButton = await messagesQuery.getReplyButton(messageId);
        setReplyButtonSelected(replyButton);

        // 団体のお知らせボタン取得
        const companyReplyButtonList = await companiesQuery.getReplyButtons(tenantId!);
        companyReplyButtonList.unshift({
          id: "undefined",
          name: "",
          buttonNumber: 0,
          buttonName: [],
          buttonColor: [],
          type: "",
          createUser: "",
          createAt: new Date(),
        });
        setCompanyReplyButtonList(companyReplyButtonList);
        setDefaultReplyButtonList(companyReplyButtonList);

        // イベント用返信ボタン作成
        const eventReplyButtons = createEvenReplyButtons();
        eventReplyButtons.forEach((button) => {
          button.tenantId = tenantId!;
        });
        setEventReplyButtonList(eventReplyButtons);

        // ZoomURL一覧取得
        const onlineUrls = await companiesQuery.getOnlineUrls(tenantId!);
        onlineUrls.unshift({
          id: "none",
          onlineType: "",
          name: "設定なし",
          url: "none",
          type: "",
          createUser: "",
          createAt: new Date()
        },
        {
          id: "input",
          onlineType: "",
          name: "直接URLを入力",
          url: "",
          type: "",
          createUser: "",
          createAt: new Date()
        });
        setOnlineUrlList(onlineUrls);

        // ミーティングURL設定
        const onlineUrlsUrl = onlineUrls.filter((url) => url.url === message.link).at(0);
        if(onlineUrlsUrl !== undefined) {
          // ミーティングURL一覧から選択されている場合
          setOnlineUrlSelected(onlineUrlsUrl.url);
        }
        if(onlineUrlsUrl === undefined && message.link !== 'none'){
          message.url = message.link;
          setOnlineUrlSelected('');
        }
        setMessage(message);
        setSelectedEventType(message.eventType ?? '');
        setBase64Image(message.image);

        setIsLoad(false);
      }
      f();
    } else {
      dispatch(
        alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
      );
    }
  }, [dispatch, messageId, tenantId, isOpen]);

  // お知らせプレビュー
  const onClickPreview = async (data: Messages) => {
    await messageDataCreate(data, 'preview');
  };

  // お知らせ登録
  const onClickSubmit = async () => {
    await messageDataCreate(previewData!, 'send');
  };

  // お知らせ下書き
  const onDelivered = async (data: Messages) => {
    await messageDataCreate(data, 'draft');
  };

  // お知らせの下書き・登録時の共通処理
  const messageDataCreate = async (data: Messages, type: 'draft' | 'send' | 'preview')
    : Promise<void> => {
    if(!isDisabled.current){
      try{
        isDisabled.current = true;
        const {sendDateList, sendData} = await messageCommonFunctions.submit(
          data, type, onlineUrlSelected, currentUser,
          selectedEventType, base64Image, company, tenantId
        );

        // Firestore登録
        sendData.draftFlg = type === 'draft' || type === 'preview';
        sendData.select = company?.displayId!;
        // 下書き更新・未読更新
        if(type === 'draft'){
          // 下書き
          // 配信フラグ
          sendData.deliveredFlg = false;
          await messagesQuery.updateMessage(sendData, replyButtonSelected);
          dispatch(alertOpen(AlertValue("下書き保存しました。", "success")));
          toggle();
          isDisabled.current = false;
        } else if(type === 'send') {
          // 配信
          await messagesQuery.deleteTopic(message?.id);
          await messagesQuery.deleteMessage(sendData.id);
          sendData.id = '';
          const result = await Promise.all(sendDateList.map( async (sendDateAndEventDateList) => {
            sendData.sendDate = new Date(sendDateAndEventDateList.sendDate);
            sendData.eventAt = sendDateAndEventDateList.eventAt ?? new Date();
            sendData.eventEndAt = sendDateAndEventDateList.eventEndAt ?? new Date();
            // 配信フラグ
            sendData.deliveredFlg =sendData.sendDate < new Date();
            // tokenのdeliverdFlg更新
            return await messagesQuery.addMessage(sendData, replyButtonSelected);
          }));
          if(result.length === 0){
            throw new Error();
          }
          dispatch(alertOpen(AlertValue("お知らせを作成しました", "success")));
          togglePreview();
          toggle();
          isDisabled.current = false;
          navigate(`/companies/${company?.id}/message/${result.at(0)}/info`);
        } else {
          // プレビュー
          setPreviewData(sendData);
          togglePreview();
          isDisabled.current = false;
        };
      } catch (err: any) {
        if (err === "ERR_NETWORK") {
          dispatch(
            alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
          );
        } else {
          console.error(err)
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        }
        isDisabled.current = false;
      }
    }
  };

  const onReplyButtonSelect = (id: string) => {
    const replyButton: ReplyButton | undefined = companyReplyButtonList?.filter(
      (item) => item.id === id)[0];
    setReplyButtonSelected(replyButton);
  }

  const onOnlineUrlSelect = (url: string) => {
    setOnlineUrlSelected(url);
  }

  const onEventTypeSelect = (eventType: string) => {
    setSelectedEventType(eventType);
  };

  const onSendImage = (file: File) => {
    try{
      if(file.type !== 'image/jpeg' && file.type !== 'image/png'){
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_UNSUPPORT_IMG_FILE_TYPE, "danger")));
        return;
      };
      messageCommonFunctions.imageFileToBase64Image(file, setBase64Image);
    } catch (error: any){
      console.error(error);
      dispatch(alertOpen(AlertValue("アップロードに失敗しました", "danger")));
    }
  };

  const onClickDelete = async () =>{
    try {
      await messagesQuery.deleteTopic(message?.id);
      await messagesQuery.deleteMessage(message.id!);
      dispatch(alertOpen(AlertValue("削除しました。", "success")));
      navigate("../message", { state: message.id! })
    } catch (err) {
      console.error(err)
      dispatch(alertOpen(AlertValue("削除に失敗しました", "danger")));
    }
  }

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const togglePreview = () => {
    setIsOpenPreview(!isOpenPreview);
  };

  return (
    <>
      <div className="flex h-full">
        <div className="relative z-0 flex flex-1 overflow-hidden">
          <main className="relative z-0 flex-1 overflow-y-auto focus:outline-none xl:order-last">
            {/* Breadcrumb */}
            <nav
              className="flex items-start px-2 py-3"
              aria-label="Breadcrumb"
            >
              <div className="inline-flex items-center text-sm font-medium text-gray-900 cursor-pointer"
                onClick={() => navigate(`/companies/${tenantId}/message`)}
              >
                <ChevronLeftIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                <span>お知らせ一覧へ</span>
              </div>
            </nav>
            {isLoad ? <Spiner /> : (
              <MessageRef
                value={message}
                topicsMap={topicsMap}
                replyButton={replyButtonSelected}
                onlineUrlList={onlineUrlList}
              />
            )}
            <div className={'flex m-5 pb-6 justify-end'}>
              {(
                message.draftFlg // 下書き
                || message.sendDate >= new Date() // 配信日が本日以降(未配信)
              ) &&
                (<>
                  <Button
                    size={"sm"}
                    className={"ml-2"}
                    onClick={toggle}
                    children={"編集"}
                  />
                  <Button
                    color={"red"}
                    size={"sm"}
                    className={"ml-2"}
                    onClick={onClickDelete}
                    children={"削除する"}
                  />
                  {(isOpen) && (<MessageEditModal
                    open={isOpen}
                    toggle={toggle}
                    closeOnClickOutside={true}
                    children={undefined}
                    message={message}
                    company={company}
                    onClickPreview={onClickPreview}
                    onDelivered={onDelivered}
                    onReplyButtonSelect={onReplyButtonSelect}
                    replyButtonList={companyReplyButtonList}
                    setReplyButtonList={setCompanyReplyButtonList}
                    defaultReplyButtonList={defaultReplyButtonList}
                    eventReplyButtonList={eventReplyButtonList}
                    replyButtonSelected={replyButtonSelected}
                    onOnlineUrlSelect={onOnlineUrlSelect}
                    onEventTypeSelect={onEventTypeSelect}
                    selectedEventType={selectedEventType}
                    onSendImage={onSendImage}
                    setBase64Image={setBase64Image}
                    base64Image={base64Image}
                    onlineUrlList={onlineUrlList}
                    onlineUrlSelected={onlineUrlSelected ?? ''}
                  />)}
                  <MessagePreviewModal
                    children={undefined}
                    open={isOpenPreview}
                    toggle={togglePreview}
                    closeOnClickOutside={false}
                    data={previewData}
                    company={company!}
                    replyButtonSelected={replyButtonSelected}
                    onClickSubmit={onClickSubmit}
                  />
                </>)}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};
